import { ContactMail, Language, PhoneAndroid, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import AppLinksPanel, { AppLink } from "./components/AppsLinksPanel";
import ContactPanel, { ContactDetail } from "./components/ContactPanel";
import SocialsPanel, { SocialLink } from "./components/SocialsPanel";
import ColorPicker from "./components/ColorPicker";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

const SOURCE_OPTIONS = [
  "PixelPilot",
  "MMS",
  "ClientMemorialPage",
  "LivestreamingWebsite",
  "MuzukuruWebsite",
  "ChemaFundraiser",
];

const TABS = [
  { label: "Social Links", icon: <Language fontSize="small" /> },
  //   { label: "Navigation", icon: <ViewColumn fontSize="small" /> },
  { label: "Contact", icon: <ContactMail fontSize="small" /> },
  { label: "App Downloads", icon: <PhoneAndroid fontSize="small" /> },
];

const FooterLinksManager: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const primaryColor = colors.primary[400];
  const secondaryColor = colors.primary[500];
  const bgColor = colors.primary[900];
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const [contactDetails, setContactDetails] = useState<ContactDetail[]>([]);
  const [appLinks, setAppLinks] = useState<AppLink[]>([]);
  const [source, setSource] = useState<string>();
  const [configId, setConfigId] = useState<number | null>(null);
  const [backgroundColor, setBackgroundColor] = useState("");
  const [textColor, setTextColor] = useState("");

  const [allConfigs, setAllConfigs] = useState<
    { id: number; source: string }[]
  >([]);

  useEffect(() => {
    const fetchAllConfigs = async () => {
      try {
        const res = await apiService.get<{ id: number; source: string }[]>(
          `/api/configurations/footer-config/`,
        );
        const configs = res.data.map((c) => ({ id: c.id, source: c.source }));
        setAllConfigs(configs);
        if (configs.length > 0) setSource(configs[0].source);
      } catch {
        console.error("Failed to fetch config list");
      }
    };
    fetchAllConfigs();
  }, []);

  useEffect(() => {
    if (!source) return;
    const fetchConfig = async () => {
      try {
        const res = await apiService.get<
          {
            id: number;
            source: string;
            socials: SocialLink[];
            contact_details: ContactDetail[];
            app_links: AppLink[];
            background_color?: string;
            text_color?: string;
          }[]
        >(`/api/configurations/footer-config/`, { source });
        const config = res.data.find((c) => c.source === source);
        if (config) {
          setConfigId(config.id);
          setSocials(config.socials.map((s) => ({ ...s, id: String(s.id) })));
          setContactDetails(
            config.contact_details.map((c) => ({ ...c, id: String(c.id) })),
          );
          setAppLinks(
            config.app_links.map((l) => ({ ...l, id: String(l.id) })),
          );
          setBackgroundColor(config.background_color ?? "");
          setTextColor(config.text_color ?? "");
        } else {
          setSocials([]);
          setContactDetails([]);
          setAppLinks([]);
          setConfigId(null);
        }
      } catch {
        toast.error("Failed to load footer config.");
      }
    };
    fetchConfig();
  }, [source]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        source,
        background_color: backgroundColor || "#000000",
        text_color: textColor || "#ffffff",
        socials: socials.map(({ platform, url, icon }, i) => ({
          platform,
          url,
          icon,
          order: i,
        })),
        contact_details: contactDetails.map(({ type, value }, i) => ({
          type,
          value,
          order: i,
        })),
        app_links: appLinks.map(({ platform, url, label }, i) => ({
          platform,
          url,
          label,
          order: i,
        })),
      };

      if (configId) {
        await apiService.patch(
          `/api/configurations/footer-config/${configId}/`,
          payload,
        );
      } else {
        const res = await apiService.post<{ id: number }>(
          `/api/configurations/footer-config/`,
          payload,
        );
        setConfigId(res.data.id);
      }

      toast.success("Footer configuration saved successfully!");
    } catch {
      toast.error("Failed to save footer configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box
        sx={{
          bgcolor: primaryColor,
          borderRadius: "12px 12px 0 0",
          p: { xs: 3, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: colors.primary[900],
                fontSize: { xs: "1.4rem", md: "1.8rem" },
              }}
            >
              Footer Links Manager
            </Typography>
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Manage app download routes, global links, and company contacts
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ColorPicker label="Footer Background Color" value={backgroundColor} onChange={setBackgroundColor} />

          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" color="white" fontWeight={600}>
              Text
            </Typography>
            <Box
              component="input"
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              sx={{
                width: 36,
                height: 36,
                border: "none",
                borderRadius: 50,
                cursor: "pointer",
                padding: 0,
                bgcolor: "transparent",
              }}
            />
          </Box> */}
        </Box>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={
              isSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save />
              )
            }
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              bgcolor: colors.primary[900],
              color: primaryColor,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              boxShadow: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
            }}
          >
            {isSaving ? "Saving..." : "Save All"}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          flexWrap: "wrap",
          bgcolor: colors.primary[800],
          border: `1px solid ${primaryColor}22`,
          borderTop: "none",
        }}
      >
        <Typography
          variant="caption"
          color={colors.primary[400]}
          fontWeight={700}
          sx={{ mr: 1 }}
        >
          SITES:
        </Typography>

        {SOURCE_OPTIONS.map((s) => {
          const exists = allConfigs.some((c) => c.source === s);
          const isActive = source === s;
          return (
            <Chip
              key={s}
              label={s}
              size="small"
              onClick={() => {
                setSource(s);
                if (!exists) {
                  setConfigId(null);
                  setSocials([]);
                  setContactDetails([]);
                  setAppLinks([]);
                }
              }}
              variant={isActive ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                borderColor: exists ? primaryColor : `${primaryColor}55`,
                borderStyle: exists ? "solid" : "dashed",
                color: isActive
                  ? colors.primary[900]
                  : exists
                    ? primaryColor
                    : colors.primary[300],
                bgcolor: isActive ? primaryColor : "transparent",
                "&:hover": { bgcolor: `${primaryColor}22` },
              }}
            />
          );
        })}
      </Box>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${primaryColor}22`,
          borderTop: "none",
          borderRadius: "0 0 12px 12px",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${primaryColor}22`,
            px: 2,
            "& .MuiTabs-indicator": { backgroundColor: primaryColor },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              color: colors.primary[300],
              "&.Mui-selected": { color: primaryColor },
            },
          }}
        >
          {TABS.map((tab, i) => (
            <Tab
              key={tab.label}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              value={i}
            />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 0 && (
                <SocialsPanel
                  socials={socials}
                  setSocials={setSocials}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  bgColor={bgColor}
                />
              )}
              {/* {activeTab === 1 && (
                <NavColumnsPanel
                  navColumns={navColumns}
                  setNavColumns={setNavColumns}
                  primaryColor={primaryColor}
                  bgColor={bgColor}
                />
              )} */}
              {activeTab === 1 && (
                <ContactPanel
                  contactDetails={contactDetails}
                  setContactDetails={setContactDetails}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  bgColor={bgColor}
                />
              )}
              {activeTab === 2 && (
                <AppLinksPanel
                  appLinks={appLinks}
                  setAppLinks={setAppLinks}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  bgColor={bgColor}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Paper>
    </Box>
  );
};

export default FooterLinksManager;
